import { AtsJobVacancyStatusEnum } from '@/generated/graphql/types';
import { useAmplitude } from '@crehana/analytics';
import { useEffect, useState } from 'react';
import { ApplicationFormStatus } from '../../shared/application-form-status';
import { Details } from '../shared/components/details';
import { Form } from '../shared/components/form';
import { useTranslationCandidateForm } from '../shared/hooks/use-translations';
import type {
  TPageLoaderOrganizationData,
  TPageLoaderVacancyData,
} from '../types';

export const CandidateAnswerForm = ({
  vacancy: jobVacancyPublicInfo,
  organization,
}: {
  vacancy: TPageLoaderVacancyData;
  organization: TPageLoaderOrganizationData;
}) => {
  const { logEvent } = useAmplitude();
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    logEvent({
      event: 'Render Candidate Form',
      data: {
        url: window.location.href,
        product: 'ATS',
      },
    });
  }, []);

  const { t } = useTranslationCandidateForm();

  const [isPublished, setIsPublished] = useState(false);

  const organizationId = jobVacancyPublicInfo?.centralized_organization_id;

  // Display a not found status if the job vacancy is not found
  if (
    (jobVacancyPublicInfo && !jobVacancyPublicInfo.id) ||
    jobVacancyPublicInfo.is_deleted ||
    jobVacancyPublicInfo.state === AtsJobVacancyStatusEnum.IsDraft
  ) {
    return (
      <div className="tw-h-[100vh]">
        <ApplicationFormStatus
          title={t('CANDIDATE_FORM_STATUS_TITLE_NOT_FOUND')}
          description={t('CANDIDATE_FORM_STATUS_DESCRIPTION_NOT_FOUND')}
          type="not-found"
        />
      </div>
    );
  }

  // Display a closed status if the job vacancy is closed
  if (
    jobVacancyPublicInfo?.state === AtsJobVacancyStatusEnum.IsClosed ||
    jobVacancyPublicInfo.is_paused
  ) {
    return (
      <div className="tw-h-[100vh]">
        <ApplicationFormStatus
          title={t('CANDIDATE_FORM_STATUS_TITLE_CLOSED')}
          subtitle={t('CANDIDATE_FORM_STATUS_SUBTITLE_CLOSED')}
          description={t('CANDIDATE_FORM_STATUS_DESCRIPTION_CLOSED')}
          type="close"
        />
      </div>
    );
  }

  // Display a success status if the application form is published
  if (isPublished) {
    return (
      <div className="tw-h-[100vh]">
        <ApplicationFormStatus
          title={t('CANDIDATE_FORM_STATUS_TITLE_SUCCESS')}
          subtitle={t('CANDIDATE_FORM_STATUS_SUBTITLE_SUCCESS')}
          description={t('CANDIDATE_FORM_STATUS_DESCRIPTION_SUCCESS')}
          type="success"
        />
      </div>
    );
  }

  return (
    <div className="tw-w-full tw-flex tw-flex-col tw-items-center tw-justify-center">
      {/**Show the job vacancy details */}
      <div className="tw-w-full">
        <Details
          jobVacancyPublicInfo={jobVacancyPublicInfo}
          name={organization.name || ''}
          logo={organization.logo || ''}
        />
      </div>
      {/**Show the application form */}
      <div
        id="application-form"
        className="tw-w-full tw-flex tw-justify-center tw-px-3.2 md:tw-px-[9.2rem] lg:tw-px-[32.4rem] tw-py-3.2 md:tw-py-6.4 lg:tw-py-7.2"
        style={{
          background:
            'linear-gradient(90deg, #F2F3F5 0%, rgba(240, 242, 255, 0.90) 100%)',
        }}
      >
        <Form
          jobVacancyPublicInfo={jobVacancyPublicInfo}
          setIsPublished={setIsPublished}
          organizationId={organizationId || 0}
          preview={false}
        />
      </div>
    </div>
  );
};
